define('home/views/HomepageView',[
    'jquery',
    'underscore',
    'backbone',
    'backbone-marionette',

    'core/strings',

    'home/models/Session',
    'home/collections/ForumCollection',
    'home/views/common/TabbedView',
    'home/views/RecommendationsModuleView',
    'home/views/ChannelCardCollectionViewWithChannelPromotion',
    'home/views/ChannelFeedFooterView',
    'home/views/common/FetchableCollectionLayout',
    'home/views/CommentCardCollectionView',
    'home/views/HomepageUserModuleView',
    'home/views/HomepageCommentsModuleView',
    'home/views/HomepageChannelsModuleView',
    'home/views/HomepageOnboardingModuleView',
    'home/views/ModeratedForumsModuleView',
    'home/views/HomepageFollowingModuleView',
    'home/views/HomepageRecommendedFeedEmptyView',
    'home/views/HomepageCommentsFeedEmptyView',
    'home/views/HomepageDiscussionsFeedEmptyView',
    'home/views/MotdAlertView',
    'home/mixins/withClickLinkTracking',
    'home/mixins/withEmailVerificationAlert',
    'home/mixins/withEmailConfirmedModal',

    'home/templates/homepage',
    'home/templates/homepageTabs',
], function (
    $,
    _,
    Backbone,
    Marionette,

    strings,

    Session,
    ForumCollection,
    TabbedView,
    RecommendationsModuleView,
    ChannelCardCollectionViewWithChannelPromotion,
    ChannelFeedFooterView,
    FetchableCollectionLayout,
    CommentCardCollectionView,
    HomepageUserModuleView,
    HomepageCommentsModuleView,
    HomepageChannelsModuleView,
    HomepageOnboardingModuleView,
    ModeratedForumsModuleView,
    HomepageFollowingModuleView,
    HomepageRecommendedFeedEmptyView,
    HomepageCommentsFeedEmptyView,
    HomepageDiscussionsFeedEmptyView,
    MotdAlertView,
    withClickLinkTracking,
    withEmailVerificationAlert,
    withEmailConfirmedModal,

    homepageTemplate,
    homepageTabsTemplate
) {
    'use strict';

    var gettext = strings.get;

    var HomepageView = TabbedView.extend({
        defaultTab: 'popular',
        template: homepageTemplate,
        templateHelpers: function () {
            return {
                onboardingWithoutChannels: this.onboardingWithoutChannels,
            };
        },


        events: _.defaults({
            'click a[data-link-name]': 'trackClickLink',
        }, TabbedView.prototype.events),

        regions: _.defaults({
            userCardRegion: '[data-role=user-card]',
            commentsModuleRegion: '[data-role=comments-module]',
            followingModuleRegion: '[data-role=following-module]',
            recommendedChannelsModuleRegion: '[data-role=recommend-module][data-recommend-type=channel]',
            recommendedUsersModuleRegion: '[data-role=recommend-module][data-recommend-type=user]',
            recommendedForumsModuleRegion: '[data-role=recommend-module][data-recommend-type=forum]',
            rightRailRegion: '[data-role=right-rail]',
            leftRailRegion: '[data-role=left-rail]',
            onboardingModuleRegion: '[data-role=onboarding-module]',
            homepageTabsRegion: '[data-role=homepage-nav]',
            moderatedForumsRegion: '[data-role=moderated-forums-module]',
            motdAlertRegion: '[data-role=motd-alert]',
        }, TabbedView.prototype.regions),

        initialize: function (options) {
            TabbedView.prototype.initialize.call(this, options);

            this.session = Session.get();

            this.showOnboarding = false;

            this.onboardingWithoutChannels = options.onboardingWithoutChannels;

            this.session.loadPromise().then(_.bind(function () {
                this.showOnboarding = this.session.isLoggedIn() && !this.session.user.get('homeFeedOnboardingComplete');
                if (this.showOnboarding)
                    this.toggleOnboardingModules();

            }, this));

            this.listenTo(this, 'change:activeTab', this.updateTabSpecificModules);
        },

        onDomRefresh: function () {
            TabbedView.prototype.onDomRefresh.call(this);

            // Keep these, don't un-render based on onboardingWithoutChannels, this would show the channels users
            // are already following which we don't want to get rid of at least for now
            var interestingChannelsFetchPromise = this.model.interestingChannels.ensureFetched();
            var boundShowChannelsModule = _.bind(this.showChannelsModule, this);
            var forumsFollowingFetchPromise;

            // Listen for unfollow curation channel events so we can refresh
            // the recommended feed.
            this.listenTo(this.session.user, 'unfollowed:curationchannel', this.refreshRecommendedFeed);

            // Show logged in modules
            this.session.loadPromise().then(_.bind(this.showLoggedInModules, this));

            forumsFollowingFetchPromise = this.session.userProfile.forumsFollowing.ensureFetched();
            forumsFollowingFetchPromise.done(_.bind(this.showFollowingModule, this));

            // when user is logged in, also wait for forumsFollowing fetch to complete so that
            // we can remove channels that a user has followed.
            $.when(forumsFollowingFetchPromise, interestingChannelsFetchPromise).done(
                boundShowChannelsModule
            );

            this.toggleOnboardingModules();
        },

        showFollowingModule: function () {
            var forumsFollowingFeed = this.session.userProfile.forumsFollowing;
            if (!forumsFollowingFeed.items.length)
                return;

            this.followingModuleRegion.show(new HomepageFollowingModuleView({
                collection: forumsFollowingFeed.items,
            }));
        },

        showMotdAlertView: function () {
            this.motdAlertRegion.show(new MotdAlertView());
        },

        showLoggedInModules: function () {
            this.recommendedUsersModuleRegion.show(
                RecommendationsModuleView.createUserRecommendedModuleView({
                    model: this.model.interestingUsers,
                })
            );

            this.userCardRegion.show(new HomepageUserModuleView({
                model: this.session.user,
            }));

            this.homepageTabsRegion.show(new Marionette.ItemView({
                template: homepageTabsTemplate,
            }));

            // update tab classes as there's a chance that the first update ran
            // before homepage tabs are rendered on the page (if session fetch returns after
            // initial render happens)
            this.updateTabClasses();

            this.showModeratedForums();
        },

        showModeratedForums: function () {
            this.session.loadModeratedForums().then(_.bind(function () {
                var models = _.chain(this.session.moderatedForums)
                    .reject(function (id) {
                        // TODO - this is a super hacky way of filtering
                        // out channels and should be fixed ASAP once we have
                        // an endpoint that returns a list of forums that a user
                        // moderates.
                        //
                        // https://trello.com/c/UoOrovOm/360-create-listmoderatedforums-endpoint-to-return-the-list-of-forums-that-a-user-moderates
                        return id.indexOf('channel-') === 0;
                    })
                    .sortBy() // alphabetically
                    .map(function (id) {
                        // Convert array of forum ids to an array
                        // of forum model attributes to be passed
                        // into ForumCollection
                        return { id: id };
                    })
                    .value();

                if (_.isEmpty(models))
                    return;

                this.moderatedForumsRegion.show(new ModeratedForumsModuleView({
                    collection: new ForumCollection(models),
                }));

                // If we display the moderationForums, also display the MOTD at the top.
                this.showMotdAlertView();
            }, this));
        },

        /*
         * Shows the Recommended Channels module except in the case that
         * you're following all of them from the given list. Then falls
         * back to the older Recommended Forums module.
         */
        showChannelsModule: function () {
            var filteredChannels = this.model.interestingChannels.items
                .chain()
                .reject(function (channelProfile) {
                    return channelProfile.channel.primaryForum.get('isFollowing');
                })
                .pluck('channel')
                .shuffle()
                .value();

            if (filteredChannels.length) {
                // Only show channels module if there are some Channels
                // the user hasn't followed yet.
                this.recommendedChannelsModuleRegion.show(new HomepageChannelsModuleView({
                    collection: new Backbone.Collection(filteredChannels),
                }));
            } else {
                // User has followed all channels, so show the recommended
                // forums module instead.
                this.model.initializeAndFetchInterestingForums();
                this.recommendedForumsModuleRegion.show(RecommendationsModuleView.createForumRecommendedModuleView({
                    model: this.model.interestingForums,
                }));
            }
        },

        refreshRecommendedFeed: function () {
            this.model.popular.fetchItems({
                reset: true,
            });
        },

        /*
         * Shows and hides modules that are specific to a particular tab based on what
         * the current active tab is.
         */
        updateTabSpecificModules: function (activeTab) {
            if (activeTab === 'comments') {
                this.hideRegion(this.commentsModuleRegion);
            } else if (!Session.isKnownToBeLoggedOut()) {
                if (!this.commentsModuleRegion.currentView)
                    this.commentsModuleRegion.show(this.createHomepageCommentsModuleView());

                this.showRegion(this.commentsModuleRegion);
            }
        },

        toggleOnboardingModules: function () {
            var regionsToDisable = ['leftRailRegion', 'rightRailRegion'];

            _.each(regionsToDisable, function (regionName) {
                this[regionName].ensureEl();
                this[regionName].$el.toggleClass('is-disabled', this.showOnboarding);
            }, this);

            if (this.showOnboarding) {
                var onboardingModuleView = new HomepageOnboardingModuleView({
                    onboardingWithoutChannels: this.onboardingWithoutChannels,
                });
                this.listenTo(onboardingModuleView, 'hideOnboarding', function () {
                    this.showOnboarding = false;
                    this.toggleOnboardingModules();
                });

                this.onboardingModuleRegion.show(onboardingModuleView);

                this.hideRegion(this.emailVerificationAlertRegion);
            } else if (this.onboardingModuleRegion.$el) {
                this.hideRegion(this.onboardingModuleRegion);
            }
        },


        createHomepageCommentsModuleView: function () {
            // Create an empty collection of posts to back the comments module.
            var postCollection = new Backbone.Collection([]);
            var self = this;

            // Once the recent comments feed has fetched, fill the collection with
            // the posts from the feed. The view will update itself.
            this.model.commentsPromise.then(function () {
                postCollection.reset(self.model.comments.items.map(function (feedItem) {
                    // The comments feed doesn't support grouped activities, so
                    // we only care about the first activity. Return the post
                    // from that activity.
                    return feedItem.activities[0].object;
                }));
            });

            return new HomepageCommentsModuleView({
                collection: postCollection,
                commentsPromise: this.model.commentsPromise,
            });
        },

        hideRegion: function (region) {
            if (region.$el)
                region.$el.addClass('is-hidden');
        },

        showRegion: function (region) {
            if (region.$el)
                region.$el.removeClass('is-hidden');
        },

        getTrackingSection: function (activeTab) {
            // Return the tab name for tracking
            return activeTab;
        },

        getTabView: function (activeTab) {
            var emptyViewOptions = {
                model: this.model.spotlightProfile.topActivities,
                collection: this.model.spotlightProfile.topActivities.items,
                maxItemsText: this.model.constructor.MAX_SPOTLIGHT_ITEMS.TEXT,
                onboardingWithoutChannels: this.onboardingWithoutChannels,
            };

            switch (activeTab) {
            case 'popular':
                return new FetchableCollectionLayout({
                    model: this.model.popular,
                    collection: this.model.popular.items,
                    fetchableCollectionView: ChannelCardCollectionViewWithChannelPromotion,
                    fetchableCollectionFooterView: ChannelFeedFooterView,
                    infiniteScroll: true,
                    enableBestOfDisqusCallout: true,
                    emptyView: HomepageRecommendedFeedEmptyView,
                    emptyViewOptions: emptyViewOptions,
                });
            case 'comments':
                return new FetchableCollectionLayout({
                    model: this.model.comments,
                    collection: this.model.comments.items,
                    fetchableCollectionView: CommentCardCollectionView,
                    infiniteScroll: true,
                    emptyView: HomepageCommentsFeedEmptyView,
                    emptyViewOptions: emptyViewOptions,
                });
            case 'discussions':
                return new FetchableCollectionLayout({
                    model: this.model.discussions,
                    collection: this.model.discussions.items,
                    fetchableCollectionView: ChannelCardCollectionViewWithChannelPromotion,
                    infiniteScroll: true,
                    emptyView: HomepageDiscussionsFeedEmptyView,
                    emptyViewOptions: emptyViewOptions,
                });
            }
        },

        getMetaAttrs: function () {
            switch (this.activeTab) {
            case 'popular':
                return gettext('Recommended Discussions');
            case 'comments':
                return gettext('Latest Comments');
            case 'discussions':
                return gettext('Latest Discussions');
            }
        },
    });

    withClickLinkTracking.call(HomepageView.prototype);
    withEmailVerificationAlert.call(HomepageView.prototype);
    withEmailConfirmedModal.call(HomepageView.prototype);

    return HomepageView;
});

